import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { theme } from '../../../theme/theme'
import { Navigate, useParams } from 'react-router-dom'
import { PreviewGuard } from '../../../components/PreviewGuard'
import { useGetGeneralPage } from '../../../api/contentful/contentfulQueries'
import { LoadingSpinner } from '../../../components/LoadingSpinner'
import {
  IHighlightedListBlockFields,
  isIFAQ,
  isIHighlightedListBlock,
  isImageBlock,
  Services,
} from '@estercare/ester-shared'
import { Header } from '../../../components/Header'
import { DynamicBlock } from '../../../components/DynamicBlock'
import { ComponentLayout } from '../../../components/layouts/ComponentLayout'
import { Wave } from '../../../components/wave/Wave'
import { getBackgroundColor, hasBlockBackgroundColorProperty } from '../../../contentful/utils'
import { OnClickButton } from '../../../components/buttons/onclick/OnClickButton'
import { useState } from 'react'
import { Success } from '../../../components/icons'
import { BodyLarge, Title3 } from '../../../components/styles/Typography'
import { ErrorSign } from '../../../components/icons/ErrorSign'
import { Spinner } from '../../../components/icons/Spinner'
import { Modal } from '../../../components/modals/Modal'
import { useSelectActiveServices, useSelectUserEmail } from '../../../state/selectors'
import { useWindowWidth } from '../../../hooks/useWindowWidth'
import { isTablet, isMobile } from '../../../utils/devices'
import { useRequestServiceActivationMutation } from '../../../api/ester-b2b-api/private/services/serviceQueries'
import { useFilteredContent } from '../../../hooks/useFilteredContent'
import { ROUTES } from '../../../constants/routes'
import { isCertificationService } from '../../../utils/typeGuards'

export const Service = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const width = useWindowWidth()
  const isMobileDevice = isTablet(width) || isMobile(width)

  const activeServices = useSelectActiveServices()
  const email = useSelectUserEmail()
  const servicePageQuery = useGetGeneralPage(id as string)
  const { mutate: sendActivationRequest } = useRequestServiceActivationMutation()

  const [showModal, setShowModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState<boolean>()
  const [buttonIcon, setButtonIcon] = useState<React.ReactNode>()

  const serviceData = servicePageQuery.data?.items[0]?.fields
  const { title, description, shortDescription, slug, contentBlocks, serviceIdentifier } = serviceData || {}
  const filteredContentBlocks = useFilteredContent(contentBlocks)

  if (servicePageQuery.isLoading) return <LoadingSpinner />
  if (!serviceData || !email) return <PreviewGuard />
  if (!id) throw new Error('No slug provided')
  // The certification service has no Contentful content blocks; admins start the certification directly.
  if (isCertificationService(serviceIdentifier)) return <Navigate to={ROUTES.ADMIN_CERTIFICATION} replace />
  if (!description || !shortDescription || !slug || !title || !contentBlocks) return <PreviewGuard />

  const isActive = activeServices.some((service) => service === serviceIdentifier)
  const waveColor = getBackgroundColor(hasBlockBackgroundColorProperty(contentBlocks?.[0]) || '')
  const buttonText = isMobileDevice ? t('admin.service.priceProposalMobile') : t('admin.service.priceProposalDesktop')

  const sendServiceMail = () => {
    setIsLoading(true)
    setShowModal(true)
    sendActivationRequest(
      { serviceName: serviceIdentifier as Services },
      {
        onSuccess: () => {
          setIsLoading(false)
          setIsSuccess(true)
          setButtonIcon(<Success color={theme.color.white} size={24} />)
        },
        onError: () => {
          setIsLoading(false)
          setIsSuccess(false)
          setButtonIcon(<ErrorSign color={theme.color.white} size={24} />)
        },
      }
    )
  }

  const closeModal = () => {
    setShowModal(false)
  }

  const renderModalContent = () => {
    if (isLoading) {
      return (
        <>
          <Spinner size={100} color={theme.color.plum} />
          <TextBlock>
            <StyledBodyLarge>{t('admin.service.loadingMessage')}</StyledBodyLarge>
          </TextBlock>
        </>
      )
    }

    if (isSuccess) {
      return (
        <>
          <Success />
          <TextBlock>
            <Title3>{t('admin.service.successTitle')}</Title3>
            <BodyLarge>{t('admin.service.successDescription')}</BodyLarge>
          </TextBlock>
        </>
      )
    }

    return (
      <>
        <ErrorSign />
        <TextBlock>
          <Title3>{t('admin.service.errorTitle')}</Title3>
          <BodyLarge>{t('admin.service.errorDescription')}</BodyLarge>
        </TextBlock>
      </>
    )
  }

  return (
    <>
      <Header
        backButtonLink="/admin/services"
        backgroundColor={theme.color.transparent}
        title={title}
        description={description}
        button={
          isActive ? (
            <HeaderButton>{t('admin.service.activeStatus')}</HeaderButton>
          ) : (
            <OnClickButton
              loading={isLoading}
              onClick={() => {
                sendServiceMail()
              }}
              icon={buttonIcon}
              text={buttonText}
              buttonStyle={{ width: isMobileDevice ? '100%' : 'auto' }}
            />
          )
        }
        noPaddingBottom
        maxWidth={740}
      />
      <StyledWaveWrapper>
        <Wave color={waveColor} />
      </StyledWaveWrapper>

      {filteredContentBlocks.map((block, index) => {
        const isListItemBlockComponent = isIHighlightedListBlock(block)
        const isFAQ = isIFAQ(block)
        const isImageBlockComponent = isImageBlock(block)
        const isListBlockComponent = isIHighlightedListBlock(block)
        const isFirstBlock = index === 0

        return (
          <ComponentLayout
            backgroundColor={
              isListItemBlockComponent || isFAQ
                ? getBackgroundColor((block.fields as IHighlightedListBlockFields).backgroundColor)
                : isImageBlockComponent
                  ? '#393F54'
                  : 'transparent'
            }
            paddingTop={isFirstBlock || isImageBlockComponent || isListBlockComponent ? 1 : 64}
            paddingBottom={isImageBlockComponent ? 0 : 64}
            key={index}
            noMaxWidth
          >
            <DynamicBlock block={block} />
          </ComponentLayout>
        )
      })}
      {showModal && (
        <Modal onClose={closeModal}>
          <ContentContainer>{renderModalContent()}</ContentContainer>
        </Modal>
      )}
    </>
  )
}

const StyledBodyLarge = styled(BodyLarge)`
  margin-top: ${theme.spacing.large}px;
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 250px;
  justify-content: center;
  width: 300px;
`
const TextBlock = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  padding-top: ${theme.spacing.medium}px;
  p {
    padding-top: ${theme.spacing.xsmall}px;
    color: ${theme.color.grey5};
  }
`

const HeaderButton = styled.button`
  display: inline-flex;
  align-self: flex-start;
  background-color: ${theme.color.green};
  padding: 6px;
  color: ${theme.color.white};
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  z-index: 5;
  position: relative;
`
const StyledWaveWrapper = styled.div`
  margin-top: -30px;
`
